import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  JobWorkOrder,
  OutsidePremises,
  ReceiveJobWorkInput,
  SettleVendorInput,
  Vendor,
  VendorPerformance,
  VendorSettlement,
  VendorStatement,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  getOutsidePremises,
  getVendorPerformance,
  getVendorStatement,
  getVendors,
  receiveJobWork,
  settleVendor,
} from './outsourcing-api.js';

/**
 * The job-work screens' data layer.
 *
 * A return or a settlement invalidates the vendor's statement AND the outside-premises list,
 * because both change the moment a piece comes back: one gains a payable, the other loses a row.
 * Showing a garment as still out after somebody has just checked it in is the mistake this
 * module exists to prevent, and it would be a strange one to make in our own cache.
 */

export const outsourcingKeys = {
  all: ['outsourcing'] as const,
  vendors: () => ['outsourcing', 'vendors'] as const,
  statement: (vendorId: string) => ['outsourcing', 'statement', vendorId] as const,
  outside: () => ['outsourcing', 'outside'] as const,
  performance: () => ['outsourcing', 'performance'] as const,
};

export function useVendors(options: { enabled?: boolean } = {}): UseQueryResult<Vendor[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: outsourcingKeys.vendors(),
    queryFn: () => getVendors(client),
    enabled: options.enabled ?? true,
  });
}

export function useOutsidePremises(
  options: { enabled?: boolean } = {},
): UseQueryResult<OutsidePremises> {
  const client = useApiClient();
  return useQuery({
    queryKey: outsourcingKeys.outside(),
    queryFn: () => getOutsidePremises(client),
    enabled: options.enabled ?? true,
  });
}

export function useVendorStatement(
  vendorId: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<VendorStatement> {
  const client = useApiClient();
  return useQuery({
    queryKey: outsourcingKeys.statement(vendorId),
    queryFn: () => getVendorStatement(client, vendorId),
    enabled: (options.enabled ?? true) && vendorId.length > 0,
  });
}

export function useVendorPerformance(
  options: { enabled?: boolean } = {},
): UseQueryResult<VendorPerformance> {
  const client = useApiClient();
  return useQuery({
    queryKey: outsourcingKeys.performance(),
    queryFn: () => getVendorPerformance(client),
    enabled: options.enabled ?? true,
  });
}

export function useReceiveJobWork(
  jobWorkId: string,
): UseMutationResult<JobWorkOrder, unknown, ReceiveJobWorkInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReceiveJobWorkInput) => receiveJobWork(client, jobWorkId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: outsourcingKeys.all });
      // The board shows the garment as off-premises; it has just come home.
      void queryClient.invalidateQueries({ queryKey: ['kanban'] });
    },
  });
}

export function useSettleVendor(
  vendorId: string,
): UseMutationResult<VendorSettlement, unknown, SettleVendorInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SettleVendorInput) => settleVendor(client, vendorId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: outsourcingKeys.all });
      // A settlement posts an expense, so the money screens have changed too.
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}
